import { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import * as Icons from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { userPermissionsAtom } from 'atoms/permissions';
import { isDrawerOpen as isDrawerOpenAtom } from 'atoms/drawer';
import { hasPermissions } from 'utils/permissions';
import { MenuItem, MenuItemWithAccessControl } from './menuItems';

interface Props {
  item: MenuItemWithAccessControl;
  showTooltip?: boolean;
  onNavigated?: () => void;
}

const getLabel = (t: (key: string) => string, item: MenuItem) => (item.nameKey ? t(item.nameKey) : item.name);

const NavMenuItem = ({ item, showTooltip = false, onNavigated }: Props) => {
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const permissions = useAtomValue(userPermissionsAtom);
  const isDrawerOpen = useAtomValue(isDrawerOpenAtom);

  const IconComponent = Icons[item.icon as keyof typeof Icons];
  const isActive = location.pathname.substring(1).split('/')[0] === item.link?.substring(1);

  const handleClick = () => {
    if (item.subMenu) {
      setSubMenuOpen(prev => !prev);
    }
    if (item.link) {
      navigate(item.link);
      onNavigated?.();
    }
  };

  const listItem = (
    <ListItem disablePadding>
      <ListItemButton disableRipple selected={isActive} onClick={handleClick}>
        <ListItemIcon>{IconComponent && <IconComponent color={isActive ? 'secondary' : 'inherit'} />}</ListItemIcon>
        <ListItemText
          primary={getLabel(t, item)}
          sx={showTooltip ? { opacity: isDrawerOpen ? 1 : 0, transition: 'opacity 150ms ease' } : undefined}
        />
        {item.subMenu ? subMenuOpen ? <ExpandLess /> : <ExpandMore /> : null}
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box>
      {showTooltip ? (
        <Tooltip
          title={getLabel(t, item)}
          placement="right"
          slots={{ transition: Zoom }}
          slotProps={{ tooltip: { sx: { fontSize: '1em', p: 1 } } }}
          arrow
        >
          {listItem}
        </Tooltip>
      ) : (
        listItem
      )}
      {item.subMenu && (
        <Collapse in={subMenuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.subMenu
              .filter(sub => hasPermissions(permissions, sub.requiredPermissions))
              .map(subItem => (
                <ListItem disablePadding key={subItem.id}>
                  <ListItemButton
                    disableRipple
                    sx={{ pl: 9 }}
                    onClick={() => {
                      if (subItem.link) {
                        navigate(subItem.link);
                        onNavigated?.();
                      }
                    }}
                  >
                    <ListItemText primary={getLabel(t, subItem)} />
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
};

export default NavMenuItem;
